/**
 * ComponentCard - File card for displaying individual assembly components
 *
 * Matches the styling from WorkSpace.jsx file cards but tailored for
 * assembly tariff results display.
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { IoDocument, IoEye } from 'react-icons/io5';
import ThumbnailImage from './ThumbnailImage';
import { FiAlertTriangle } from 'react-icons/fi';

const BASE_URL = process.env.REACT_APP_BE_MAIN_URL;

export default function ComponentCard({ component, onClick, id }) {
    const {
        file_id,
        filename,
        hts_code,
        hts_description,
        material,
        weight_kg,
        confidence,
        sec301_applies,
        duty_rate,
        s3_url,
        part_number,
        revision,
        description
    } = component;

    const token = sessionStorage.getItem("userToken");

    // Format HTS code for display (XXXX.XX.XXXX)
    const formatHtsCode = (code) => {
        if (!code || code === 'Unknown') return 'Unknown';
        // Remove any existing dots and format consistently
        const clean = code.replace(/\./g, '');
        if (clean.length >= 10) {
            return `${clean.slice(0, 4)}.${clean.slice(4, 6)}.${clean.slice(6)}`;
        }
        return code;
    };

    // Truncate filename for display
    const displayFilename = filename?.length > 40
        ? filename.substring(0, 37) + '...'
        : filename;

    // Handle thumbnail click to open PDF
    const handleThumbnailClick = (e) => {
        e.stopPropagation();
        if (s3_url) {
            window.open(s3_url, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <div id={id} className="border card p-5 transition-all hover:border-green-800">
            {/* Filename header */}
            <div className="flex items-start gap-3 p-3">
                <h3
                    className="text-sm font-semibold text-gray-900 truncate"
                    title={filename}
                >
                    {displayFilename || 'Unknown File'}
                </h3>
                {sec301_applies && (
                    <span className="flex-shrink-0 px-1.5 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-700 flex items-center gap-1">
                        <FiAlertTriangle className="w-3 h-3" />
                        301
                    </span>
                )}
            </div>

            {/* Part Info - Part Number, Revision, Description */}
            {(part_number || revision || description) && (
                <div className="px-3 pb-2 space-y-1">
                    {/* Part Number and Revision on same line */}
                    {(part_number || revision) && (
                        <div className="flex items-center gap-3 text-xs">
                            {part_number && (
                                <span className="text-gray-700">
                                    <span className="text-gray-500">P/N:</span>{' '}
                                    <span className="font-medium">{part_number}</span>
                                </span>
                            )}
                            {revision && (
                                <span className="text-gray-700">
                                    <span className="text-gray-500">Rev:</span>{' '}
                                    <span className="font-medium">{revision}</span>
                                </span>
                            )}
                        </div>
                    )}
                    {/* Part Description */}
                    {description && (
                        <p className="text-xs text-gray-600 line-clamp-2" title={description}>
                            {description}
                        </p>
                    )}
                </div>
            )}

            {/* Thumbnail - clickable to open PDF */}
            <div className="px-3">
                <div
                    className={`h-44 w-full bg-gray-100 rounded-lg overflow-hidden border ${
                        s3_url ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''
                    }`}
                    onClick={handleThumbnailClick}
                    title={s3_url ? 'Click to open PDF' : ''}
                >
                    {s3_url ? (
                        <ThumbnailImage
                            fileId={file_id}
                            filename={filename}
                            s3Url={s3_url}
                        />
                    ) : (
                        <div className="flex h-full items-center justify-center">
                            <IoDocument className="w-10 h-10 text-gray-400" />
                        </div>
                    )}
                </div>
            </div>

            {/* HTS Code + Info */}
            <div className="p-3">
                {/* HTS Code Badge - styled like classification label */}
                <div className="flex items-center gap-2">
                    <span
                        className={`px-2 py-1 text-xs font-medium rounded font-mono ${
                            hts_code && hts_code !== 'Unknown'
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-gray-100 text-gray-500'
                        }`}
                    >
                        {formatHtsCode(hts_code) || 'Unknown'}
                    </span>
                    {confidence > 0 && (
                        <span className="text-xs text-gray-500">
                            ({confidence}%)
                        </span>
                    )}
                </div>

                {/* Duty Rate Badge */}
                {duty_rate > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-purple-50 text-purple-700 border border-purple-200">
                            Duty: {duty_rate}%
                        </span>
                    </div>
                )}

                {/* Material Badge */}
                {material && material !== 'Unknown' && (
                    <div className="mt-2 text-xs text-blue-800 font-medium bg-blue-50 px-2 py-1 rounded inline-block">
                        Material: {material}
                    </div>
                )}

                {/* Weight */}
                {weight_kg && (
                    <div className="mt-2 text-xs text-gray-600">
                        Weight: <span className="font-medium">{weight_kg.toFixed(2)} kg</span>
                    </div>
                )}

                {/* Tariff Description */}
                {hts_description && (
                    <p className="mt-2 text-sm text-gray-600 line-clamp-3">
                        {hts_description}
                    </p>
                )}
            </div>

            {/* View Analysis Button */}
            <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                <button
                    onClick={() => onClick(component)}
                    className="ml-auto flex items-center gap-1.5 text-sm font-medium text-green-800 hover:bg-green-50 px-3 py-1.5 rounded-lg transition-colors"
                >
                    <IoEye className="w-5 h-5" />
                    View Analysis
                </button>
            </div>
        </div>
    );
}
